'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { LocalizedLink } from './LocalizedLink';
import { LanguageSwitcher } from './LanguageSwitcher';
import { AuthMenu } from './HeaderAuth';
import { useTranslations } from '@/hooks/useTranslations';
import { SearchAutocomplete } from './SearchAutocomplete';

const GAME_MENU_ITEMS = [
  { labelKey: 'games.menu.bestSlots', href: '/games/best-slots' },
  { labelKey: 'games.menu.bigSlotWins', href: '/games/big-slot-wins' },
  { labelKey: 'games.menu.crashCasinoGames', href: '/games/crash-casino-games' },
  { labelKey: 'games.menu.freeDemoSlots', href: '/games/free-demo-slots' },
  { labelKey: 'games.menu.gamesOfTheYear', href: '/games/games-of-the-year' },
  { labelKey: 'games.menu.megawaysSlots', href: '/games/megaways-slots' },
  { labelKey: 'games.menu.mobileSlots', href: '/games/mobile-slots' },
  { labelKey: 'games.menu.newSlots', href: '/games/new-slots' },
] as const;

function MenuIcon({ open }: { open: boolean }) {
  return (
    <span className="flex h-6 w-6 flex-col justify-center gap-1" aria-hidden>
      <span
        className={`h-0.5 w-5 rounded-full bg-current transition-transform ${open ? 'translate-y-1.5 rotate-45' : ''}`}
      />
      <span className={`h-0.5 w-5 rounded-full bg-current transition-opacity ${open ? 'opacity-0' : ''}`} />
      <span
        className={`h-0.5 w-5 rounded-full bg-current transition-transform ${open ? '-translate-y-1.5 -rotate-45' : ''}`}
      />
    </span>
  );
}

export function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [mobileGamesOpen, setMobileGamesOpen] = React.useState(false);
  const [desktopGamesOpen, setDesktopGamesOpen] = React.useState(false);
  const { t } = useTranslations();

  const closeMenus = React.useCallback(() => {
    setMenuOpen(false);
    setMobileGamesOpen(false);
    setDesktopGamesOpen(false);
  }, []);

  // Header lives in the shared layout and does not remount on client navigations.
  // Close mobile + Games dropdown whenever the route changes.
  React.useEffect(() => {
    closeMenus();
  }, [pathname, closeMenus]);

  React.useEffect(() => {
    if (!menuOpen) {
      setMobileGamesOpen(false);
    }
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0b0d19]/80 backdrop-blur">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-6 px-6 py-4">
        {/* Hamburger: before logo, only on small/medium (hidden lg+) */}
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-white lg:hidden"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <MenuIcon open={menuOpen} />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-6">
          <LocalizedLink href="/" className="shrink-0 text-xl font-bold text-white" onClick={closeMenus}>
            Hotlist<span className="text-[#ffb74d]">Casino</span>
          </LocalizedLink>
          <SearchAutocomplete
            className="relative hidden w-[320px] md:block"
            inputClassName="w-full rounded-full border border-white/10 bg-white/5 py-2 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#ffb74d] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/30"
          />
        </div>

        <nav className="hidden items-center gap-5 text-sm text-slate-300 lg:flex">
          <LocalizedLink href="/casinos" className="hover:text-white">
            {t('nav.casinos')}
          </LocalizedLink>
          <div
            className="relative"
            onMouseEnter={() => setDesktopGamesOpen(true)}
            onMouseLeave={() => setDesktopGamesOpen(false)}
            onFocus={() => setDesktopGamesOpen(true)}
            onBlur={(e) => {
              // Close only when focus leaves the whole Games dropdown group.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setDesktopGamesOpen(false);
              }
            }}
          >
            <LocalizedLink
              href="/games"
              className="inline-flex items-center gap-1 hover:text-white"
              aria-expanded={desktopGamesOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                closeMenus();
                // Prevent sticky focus after Next.js client navigation from keeping the dropdown open.
                (e.currentTarget as HTMLAnchorElement).blur();
              }}
            >
              <span>{t('nav.games')}</span>
              <svg
                className={`h-3 w-3 text-slate-400 transition-transform ${desktopGamesOpen ? 'rotate-180 text-white' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </LocalizedLink>
            <div
              className={`absolute left-0 top-full z-50 w-56 pt-2 transition-all ${
                desktopGamesOpen
                  ? 'pointer-events-auto visible opacity-100'
                  : 'pointer-events-none invisible opacity-0'
              }`}
              role="menu"
            >
              <div className="rounded-md border border-white/10 bg-[#1f3f5d]/95 py-1 shadow-xl">
                {GAME_MENU_ITEMS.map((item) => (
                  <LocalizedLink
                    key={item.href}
                    href={item.href}
                    className="block px-3 py-1.5 text-sm text-white/95 hover:bg-white/10"
                    role="menuitem"
                    onClick={(e) => {
                      closeMenus();
                      (e.currentTarget as HTMLAnchorElement).blur();
                    }}
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                ))}
              </div>
            </div>
          </div>
          <LocalizedLink href="/game-providers" className="hover:text-white">
            {t('nav.gameProviders')}
          </LocalizedLink>
          <LocalizedLink href="/blog" className="hover:text-white">
            {t('nav.blog')}
          </LocalizedLink>
          <LocalizedLink href="/contact" className="hover:text-white">
            {t('nav.contact')}
          </LocalizedLink>
        </nav>

        <span className="hidden h-5 w-px shrink-0 bg-white/10 lg:block" aria-hidden />

        <div className="flex shrink-0 items-center gap-4 text-sm text-slate-300">
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>
          <AuthMenu />
        </div>
      </div>

      {/* Mobile menu panel (below header, lg:hidden). When closed, inert removes from tab order and a11y tree. */}
      <div
        className={`border-t border-white/10 bg-[#0b0d19]/95 backdrop-blur transition-[max-height] duration-200 ease-out lg:hidden ${menuOpen ? 'max-h-[70vh] overflow-y-auto' : 'max-h-0 overflow-hidden'}`}
        inert={!menuOpen}
      >
        <nav className="flex flex-col gap-1 px-6 py-4">
          <div className="pb-2">
            <LanguageSwitcher align="left" />
          </div>
          <SearchAutocomplete
            className="relative md:hidden"
            inputClassName="w-full rounded-full border border-white/10 bg-white/5 py-2.5 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#ffb74d] focus:outline-none"
            onSubmit={closeMenus}
          />
          <LocalizedLink
            href="/casinos"
            className="rounded-lg px-3 py-2.5 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={closeMenus}
          >
            {t('nav.casinos')}
          </LocalizedLink>
          <div className="rounded-lg px-3 py-2.5 text-slate-300 hover:bg-white/10 hover:text-white">
            <div className="flex items-center justify-between gap-2">
              <LocalizedLink
                href="/games"
                className="flex-1 text-left"
                onClick={closeMenus}
              >
                {t('nav.games')}
              </LocalizedLink>
              <button
                type="button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white"
                aria-label={mobileGamesOpen ? t('games.hideFilters') : t('games.showFilters')}
                aria-expanded={mobileGamesOpen}
                onClick={() => setMobileGamesOpen((o) => !o)}
              >
                <svg
                  className={`h-3 w-3 transition-transform ${mobileGamesOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {mobileGamesOpen && (
              <div className="mt-2 flex flex-col gap-1 border-t border-white/5 pt-2 text-sm">
                {GAME_MENU_ITEMS.map((item) => (
                  <LocalizedLink
                    key={item.href}
                    href={item.href}
                    className="rounded px-2 py-1 text-slate-400 hover:bg-white/10 hover:text-white"
                    onClick={closeMenus}
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                ))}
              </div>
            )}
          </div>
          <LocalizedLink
            href="/game-providers"
            className="rounded-lg px-3 py-2.5 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={closeMenus}
          >
            {t('nav.gameProviders')}
          </LocalizedLink>
          <LocalizedLink
            href="/blog"
            className="rounded-lg px-3 py-2.5 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={closeMenus}
          >
            {t('nav.blog')}
          </LocalizedLink>
          <LocalizedLink
            href="/contact"
            className="rounded-lg px-3 py-2.5 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={closeMenus}
          >
            {t('nav.contact')}
          </LocalizedLink>
        </nav>
      </div>
    </header>
  );
}
